import {
  criarTabelaPrecoSchema,
  novaVigenciaSchema,
  percentualParaTaxa,
  perfilTributarioSchema,
  type NovaVigenciaDto,
  type PerfilTributarioDto,
  type PerfilTributarioResponse,
  type SimplesConfigDto,
  type TabelaPrecoResponse,
  type TributoPerfil,
  type VigenciaResponse,
} from '@triade/contracts';
import { ApiError } from '@/lib/api';
import { fracaoParaPercentualTexto } from '@/lib/format';

export type DespesaTipo = NovaVigenciaDto['despesas'][number]['kind'];

export const TIPO_DESPESA_ROTULO: Record<DespesaTipo, string> = { TRIBUTO: 'Tributo', COMISSAO: 'Comissão', OUTRA: 'Outra' };

/** Despesa variavel do modo estruturado: tributo nao entra aqui (vai na aba Tributos). */
export const TIPO_DESPESA_VARIAVEL: DespesaTipo[] = ['COMISSAO', 'OUTRA'];

type DespesaCadastro = NovaVigenciaDto['despesas'][number];
/** "É tributo?": '' = nao e tributo (taxCode null). So no legado. */
export type TaxCodeEntrada = '' | NonNullable<DespesaCadastro['taxCode']>;
export type BaseDespesa = DespesaCadastro['base'];

export const TAXCODE_ROTULO: Record<TaxCodeEntrada, string> = { '': '—', ICMS: 'ICMS', ISS: 'ISS', PIS: 'PIS', COFINS: 'COFINS', OUTRO: 'Outro (IRPJ, CSLL, DAS)' };
export const BASE_DESPESA_ROTULO: Record<BaseDespesa, string> = { PRECO_SEM_TRIBUTOS: 'Preço sem tributos', TOTAL_NF: 'Total da NF' };

/** Aviso (nao bloqueia): despesa do tipo TRIBUTO sem ICMS/ISS/PIS/COFINS marcado. So no legado. */
export const AVISO_TRIBUTO_NAO_MARCADO =
  'Despesa de tributo sem marcar ICMS/ISS/PIS/COFINS/Outro: a partir de 2027 ela não recebe o fator de transição do ano.';

/** Linha da grade de despesas, como o usuario digita (percentual em pontos: "18" = 18%). */
export interface DespesaEntrada {
  tipo: DespesaTipo;
  descricao: string;
  percentual: string;
  taxCode: TaxCodeEntrada;
  base: BaseDespesa;
}

// ---- Perfil tributario (modo ESTRUTURADO): tudo como o usuario digita; a conversao para fracao e so `percentualParaTaxa` ----

export type RegimeEntrada = PerfilTributarioDto['regimeApuracao'];
export const REGIME_ROTULO: Record<RegimeEntrada, string> = {
  HERDAR_DA_EMPRESA: 'Herdar da empresa',
  SIMPLES: 'Simples Nacional',
  SIMPLES_HIBRIDO: 'Simples híbrido',
  PRESUMIDO: 'Lucro presumido',
  REAL: 'Lucro real',
};

export const META_LUCRO_ROTULO: Record<PerfilTributarioDto['metaLucro'], string> = {
  ANTES_IR: 'antes do IR',
  LIQUIDA_IR: 'líquida de IR e CSLL',
};

export const INCIDENCIA_ROTULO: Record<'DENTRO' | 'FORA' | 'LUCRO', string> = {
  DENTRO: 'dentro do preço',
  FORA: 'por fora',
  LUCRO: 'sobre o lucro',
};

/** Rotulos de linha de tributo (o ICMS e o interno da UF de origem, o resto do ICMS vem na grade). */
export const TRIBUTO_ROTULO: Partial<Record<TributoPerfil, string>> = {
  ICMS: 'ICMS interno (venda dentro da UF de origem)',
  PIS: 'PIS',
  COFINS: 'COFINS',
  IPI: 'IPI',
  ISS: 'ISS',
  IRPJ: 'IRPJ',
  CSLL: 'CSLL',
  DAS: 'DAS (Simples)',
};

/** Linhas que a tela sempre mostra (as outras, FCP/DIFAL/ICMS-ST, so aparecem se a vigencia ja tiver). */
export const TRIBUTOS_LINHA_PADRAO: TributoPerfil[] = ['ICMS', 'PIS', 'COFINS', 'IPI', 'ISS', 'IRPJ', 'CSLL', 'DAS'];

export interface LinhaTributoEntrada {
  tributo: TributoPerfil;
  aliquota: string;
  incidencia: 'DENTRO' | 'FORA' | 'LUCRO';
  base: BaseDespesa;
  ativo: boolean;
}

/** Excecao de uma UF de destino na grade. Ausente da grade = vale a tabela legal (icms-legal). Campo vazio = legal para aquele campo. */
export interface ExcecaoUfEntrada {
  interestadual: string;
  internaDestino: string;
  fcp: string;
  reducaoBase: string;
  /** Sessao 52: MVA original da ST para este destino (vazio = nao informa; vence a MVA do item). */
  mva: string;
  observacao: string;
}

export interface PerfilEntrada {
  regimeApuracao: RegimeEntrada;
  metaLucro: PerfilTributarioDto['metaLucro'];
  /** '' = UF da empresa. */
  ufOrigem: string;
  aliqLucroReal: string;
  presuncaoIrpjBens: string;
  presuncaoIrpjServicos: string;
  presuncaoCsllBens: string;
  presuncaoCsllServicos: string;
  aliqIrpj: string;
  aliqCsll: string;
  adicionalIrpj: boolean;
  acrescimoLc224: boolean;
  dasEfetiva: string;
  dasEfetivaSemCbsIbs: string;
  icmsFixo: boolean;
  derivacaoUf: PerfilTributarioDto['derivacaoUf'];
  /** Sessao 52: DIFAL com base dupla (nao contribuinte em outra UF). Desligado = base unica. */
  difalBaseDupla: boolean;
  /** Sessao 52: Simples calculado (DIGITADA = dasEfetiva, como antes; CALCULADA = Anexos da LC 123 e RBT12 da empresa). */
  simplesModoEfetiva: SimplesConfigDto['modoEfetiva'];
  simplesAnexoBem: SimplesConfigDto['anexoBem'];
  simplesAnexoServico: SimplesConfigDto['anexoServico'];
  tributos: LinhaTributoEntrada[];
  grade: Record<string, ExcecaoUfEntrada>;
}

/** Incidencia padrao da linha: IPI por fora, IRPJ/CSLL sobre o lucro, o resto dentro. Padrao de tela, nao e regra de calculo. */
const incidenciaPadrao = (t: TributoPerfil): LinhaTributoEntrada['incidencia'] =>
  t === 'IPI' ? 'FORA' : t === 'IRPJ' || t === 'CSLL' ? 'LUCRO' : 'DENTRO';

/** Perfil vazio: os campos numericos vazios ficam de fora do corpo e a API aplica o padrao da lei. */
export function perfilVazio(): PerfilEntrada {
  return {
    regimeApuracao: 'HERDAR_DA_EMPRESA',
    metaLucro: 'ANTES_IR',
    ufOrigem: '',
    aliqLucroReal: '',
    presuncaoIrpjBens: '',
    presuncaoIrpjServicos: '',
    presuncaoCsllBens: '',
    presuncaoCsllServicos: '',
    aliqIrpj: '',
    aliqCsll: '',
    adicionalIrpj: false,
    acrescimoLc224: false,
    dasEfetiva: '',
    dasEfetivaSemCbsIbs: '',
    icmsFixo: false,
    derivacaoUf: 'LIQUIDO_DENTRO',
    difalBaseDupla: false,
    simplesModoEfetiva: 'DIGITADA',
    simplesAnexoBem: 'I',
    simplesAnexoServico: 'FATOR_R',
    tributos: TRIBUTOS_LINHA_PADRAO.map((t) => ({ tributo: t, aliquota: '', incidencia: incidenciaPadrao(t), base: 'PRECO_SEM_TRIBUTOS', ativo: true })),
    grade: {},
  };
}

/** Perfil da API (vigencia em aberto) como texto de campo: fracao -> percentual digitado, sem conta. */
export function perfilDeResposta(p: PerfilTributarioResponse): PerfilEntrada {
  const linhas = new Map<TributoPerfil, LinhaTributoEntrada>(
    perfilVazio().tributos.map((l) => [l.tributo, l]),
  );
  for (const t of p.tributos) {
    linhas.set(t.tributo, { tributo: t.tributo, aliquota: fracaoParaPercentualTexto(t.aliquota), incidencia: t.incidencia, base: t.base, ativo: t.ativo });
  }
  const grade: Record<string, ExcecaoUfEntrada> = {};
  for (const x of p.icmsUf) {
    grade[x.uf] = {
      interestadual: fracaoParaPercentualTexto(x.interestadual),
      internaDestino: fracaoParaPercentualTexto(x.internaDestino),
      fcp: fracaoParaPercentualTexto(x.fcp),
      reducaoBase: fracaoParaPercentualTexto(x.reducaoBase),
      mva: fracaoParaPercentualTexto(x.mva),
      observacao: x.observacao,
    };
  }
  return {
    regimeApuracao: p.regimeApuracao,
    metaLucro: p.metaLucro,
    ufOrigem: p.ufOrigem ?? '',
    aliqLucroReal: fracaoParaPercentualTexto(p.aliqLucroReal),
    presuncaoIrpjBens: fracaoParaPercentualTexto(p.presuncaoIrpjBens),
    presuncaoIrpjServicos: fracaoParaPercentualTexto(p.presuncaoIrpjServicos),
    presuncaoCsllBens: fracaoParaPercentualTexto(p.presuncaoCsllBens),
    presuncaoCsllServicos: fracaoParaPercentualTexto(p.presuncaoCsllServicos),
    aliqIrpj: fracaoParaPercentualTexto(p.aliqIrpj),
    aliqCsll: fracaoParaPercentualTexto(p.aliqCsll),
    adicionalIrpj: p.adicionalIrpj,
    acrescimoLc224: p.acrescimoLc224,
    dasEfetiva: fracaoParaPercentualTexto(p.dasEfetiva),
    dasEfetivaSemCbsIbs: fracaoParaPercentualTexto(p.dasEfetivaSemCbsIbs),
    icmsFixo: p.icmsFixo,
    derivacaoUf: p.derivacaoUf,
    difalBaseDupla: p.difalBaseDupla ?? false,
    simplesModoEfetiva: p.simples?.modoEfetiva ?? 'DIGITADA',
    simplesAnexoBem: p.simples?.anexoBem ?? 'I',
    simplesAnexoServico: p.simples?.anexoServico ?? 'FATOR_R',
    tributos: [...linhas.values()],
    grade,
  };
}

/** Formulario "Nova vigencia" (estruturada). Legado usa o mesmo objeto (so `paraNovaVigenciaLegada` le as despesas com tributo). */
export interface NovaVigenciaEntrada {
  validFrom: string;
  lucroDesejado: string;
  lucroMinimo: string;
  motivo: string;
  despesas: DespesaEntrada[];
  perfil: PerfilEntrada;
}

export const DESPESA_VAZIA: DespesaEntrada = { tipo: 'TRIBUTO', descricao: '', percentual: '', taxCode: '', base: 'PRECO_SEM_TRIBUTOS' };
export const DESPESA_VARIAVEL_VAZIA: DespesaEntrada = { tipo: 'COMISSAO', descricao: '', percentual: '', taxCode: '', base: 'PRECO_SEM_TRIBUTOS' };

/** Despesa de tributo sem ICMS/ISS/PIS/COFINS marcado (o aviso da tela; a API nao bloqueia). So no legado. */
export const tributoNaoMarcado = (linha: Pick<DespesaEntrada, 'tipo' | 'taxCode' | 'descricao'>): boolean =>
  linha.tipo === 'TRIBUTO' && linha.taxCode === '' && linha.descricao.trim() !== '';

export const hojeIso = (): string => new Date().toLocaleDateString('en-CA');

export function novaVigenciaVazia(perfil: PerfilEntrada = perfilVazio()): NovaVigenciaEntrada {
  return { validFrom: hojeIso(), lucroDesejado: '', lucroMinimo: '', motivo: '', despesas: [], perfil };
}

export type Resultado<T> = { ok: true; dados: T } | { ok: false; mensagem: string };

const vazio = (texto: string): boolean => texto.trim() === '';

/** Vazio = null (vale o padrao da empresa, DP6). Digitado: vira fracao via `percentualParaTaxa`. */
function taxaOpcional(texto: string, rotulo: string): Resultado<string | null> {
  if (vazio(texto)) return { ok: true, dados: null };
  const taxa = percentualParaTaxa(texto);
  if (taxa === null) return { ok: false, mensagem: `${rotulo}: informe um percentual entre 0 e 100` };
  return { ok: true, dados: taxa };
}

/** Campo opcional do perfil: vazio = fora do corpo (a API aplica o padrao da lei). */
function campoOpcional(texto: string, rotulo: string): Resultado<string | undefined> {
  if (vazio(texto)) return { ok: true, dados: undefined };
  const taxa = percentualParaTaxa(texto);
  if (taxa === null) return { ok: false, mensagem: `${rotulo}: informe um percentual entre 0 e 100` };
  return { ok: true, dados: taxa };
}

/** Monta o perfil tributario do formulario. Sem conta: so converte texto digitado em fracao e valida com o contrato. */
export function paraPerfilTributario(p: PerfilEntrada): Resultado<PerfilTributarioDto> {
  const campos: Array<[keyof PerfilEntrada, string]> = [
    ['aliqLucroReal', 'Alíquota do IRPJ no lucro real'],
    ['presuncaoIrpjBens', 'Presunção IRPJ (bens)'],
    ['presuncaoIrpjServicos', 'Presunção IRPJ (serviços)'],
    ['presuncaoCsllBens', 'Presunção CSLL (bens)'],
    ['presuncaoCsllServicos', 'Presunção CSLL (serviços)'],
    ['aliqIrpj', 'Alíquota do IRPJ'],
    ['aliqCsll', 'Alíquota da CSLL'],
    ['dasEfetiva', 'DAS efetivo'],
    ['dasEfetivaSemCbsIbs', 'DAS efetivo sem CBS/IBS'],
  ];
  const parcial: Record<string, string | undefined> = {};
  const calculada = p.simplesModoEfetiva === 'CALCULADA';
  for (const [chave, rotulo] of campos) {
    // Sessao 52: com a efetiva CALCULADA o DAS digitado some da tela, entao nao vai no corpo.
    if (calculada && chave === 'dasEfetiva') continue;
    const r = campoOpcional(p[chave] as string, rotulo);
    if (!r.ok) return r;
    if (r.dados !== undefined) parcial[chave] = r.dados;
  }

  const tributos: NonNullable<PerfilTributarioDto['tributos']> = [];
  for (const l of p.tributos) {
    if (vazio(l.aliquota)) continue;
    const r = campoOpcional(l.aliquota, TRIBUTO_ROTULO[l.tributo] ?? l.tributo);
    if (!r.ok) return r;
    tributos.push({ tributo: l.tributo, aliquota: r.dados ?? '0', incidencia: l.incidencia, base: l.base, ativo: l.ativo });
  }

  const icmsUf: NonNullable<PerfilTributarioDto['icmsUf']> = [];
  for (const [uf, x] of Object.entries(p.grade)) {
    const inter = campoOpcional(x.interestadual, `ICMS interestadual (${uf})`);
    if (!inter.ok) return inter;
    const interna = campoOpcional(x.internaDestino, `Alíquota interna (${uf})`);
    if (!interna.ok) return interna;
    const fcp = campoOpcional(x.fcp, `FCP (${uf})`);
    if (!fcp.ok) return fcp;
    const reducao = campoOpcional(x.reducaoBase, `Redução de base (${uf})`);
    if (!reducao.ok) return reducao;
    const mva = campoOpcional(x.mva, `MVA original (${uf})`);
    if (!mva.ok) return mva;
    icmsUf.push({
      uf: uf as NonNullable<PerfilTributarioDto['icmsUf']>[number]['uf'],
      ...(inter.dados !== undefined ? { interestadual: inter.dados } : {}),
      ...(interna.dados !== undefined ? { internaDestino: interna.dados } : {}),
      ...(fcp.dados !== undefined ? { fcp: fcp.dados } : {}),
      ...(reducao.dados !== undefined ? { reducaoBase: reducao.dados } : {}),
      ...(mva.dados !== undefined ? { mva: mva.dados } : {}),
      observacao: x.observacao.trim(),
    });
  }

  const bruto = {
    regimeApuracao: p.regimeApuracao,
    metaLucro: p.metaLucro,
    ...(p.ufOrigem ? { ufOrigem: p.ufOrigem } : {}),
    ...parcial,
    adicionalIrpj: p.adicionalIrpj,
    acrescimoLc224: p.acrescimoLc224,
    icmsFixo: p.icmsFixo,
    derivacaoUf: p.derivacaoUf,
    difalBaseDupla: p.difalBaseDupla,
    simples: { modoEfetiva: p.simplesModoEfetiva, anexoBem: p.simplesAnexoBem, anexoServico: p.simplesAnexoServico },
    tributos,
    icmsUf,
  };
  const valido = perfilTributarioSchema.safeParse(bruto);
  if (!valido.success) return { ok: false, mensagem: valido.error.issues[0]?.message ?? 'Confira os parâmetros tributários' };
  return { ok: true, dados: valido.data };
}

/** Monta o corpo de POST /tabelas-preco/:id/vigencias no modo ESTRUTURADO (padrao). Vazios de lucro = null (padrao da empresa). */
export function paraNovaVigencia(e: NovaVigenciaEntrada): Resultado<NovaVigenciaDto> {
  if (vazio(e.validFrom)) return { ok: false, mensagem: 'Informe o início da vigência' };
  const desejado = taxaOpcional(e.lucroDesejado, 'Lucro desejado');
  if (!desejado.ok) return desejado;
  const minimo = taxaOpcional(e.lucroMinimo, 'Lucro mínimo');
  if (!minimo.ok) return minimo;

  const despesas: NonNullable<NovaVigenciaDto['despesas']> = [];
  for (const linha of e.despesas) {
    if (vazio(linha.descricao) && vazio(linha.percentual)) continue;
    if (linha.tipo === 'TRIBUTO') return { ok: false, mensagem: `Despesa "${linha.descricao.trim() || '—'}": tributo vai na aba Tributos` };
    if (vazio(linha.descricao)) return { ok: false, mensagem: 'Despesa sem descrição' };
    const taxa = percentualParaTaxa(linha.percentual);
    if (taxa === null) return { ok: false, mensagem: `Despesa "${linha.descricao.trim()}": informe um percentual entre 0 e 100` };
    despesas.push({ description: linha.descricao.trim(), kind: linha.tipo, rate: taxa, base: linha.base });
  }

  const perfil = paraPerfilTributario(e.perfil);
  if (!perfil.ok) return perfil;

  const bruto = {
    modo: 'ESTRUTURADO' as const,
    validFrom: e.validFrom,
    desiredProfitRate: desejado.dados,
    minimumProfitRate: minimo.dados,
    despesas,
    perfilTributario: perfil.dados,
    ...(vazio(e.motivo) ? {} : { motivo: e.motivo.trim() }),
  };
  const valido = novaVigenciaSchema.safeParse(bruto);
  if (!valido.success) return { ok: false, mensagem: valido.error.issues[0]?.message ?? 'Confira os campos da vigência' };
  return { ok: true, dados: valido.data };
}

/** Formulario LEGADO (tributo como despesa variavel). So para editar vigencia legada ja existente; nunca em tabela nova. */
export function paraNovaVigenciaLegada(e: NovaVigenciaEntrada): Resultado<NovaVigenciaDto> {
  if (vazio(e.validFrom)) return { ok: false, mensagem: 'Informe o início da vigência' };
  const desejado = taxaOpcional(e.lucroDesejado, 'Lucro desejado');
  if (!desejado.ok) return desejado;
  const minimo = taxaOpcional(e.lucroMinimo, 'Lucro mínimo');
  if (!minimo.ok) return minimo;

  const despesas: NovaVigenciaDto['despesas'] = [];
  for (const linha of e.despesas) {
    if (vazio(linha.descricao) && vazio(linha.percentual)) continue;
    if (vazio(linha.descricao)) return { ok: false, mensagem: 'Despesa sem descrição' };
    const taxa = percentualParaTaxa(linha.percentual);
    if (taxa === null) return { ok: false, mensagem: `Despesa "${linha.descricao.trim()}": informe um percentual entre 0 e 100` };
    despesas.push({
      description: linha.descricao.trim(),
      kind: linha.tipo,
      rate: taxa,
      taxCode: linha.taxCode === '' ? null : linha.taxCode,
      base: linha.base,
    });
  }

  const bruto: NovaVigenciaDto = {
    modo: 'LEGADO',
    validFrom: e.validFrom,
    desiredProfitRate: desejado.dados,
    minimumProfitRate: minimo.dados,
    despesas,
    ...(vazio(e.motivo) ? {} : { motivo: e.motivo.trim() }),
  };
  const valido = novaVigenciaSchema.safeParse(bruto);
  if (!valido.success) return { ok: false, mensagem: valido.error.issues[0]?.message ?? 'Confira os campos da vigência' };
  return { ok: true, dados: valido.data };
}

/** Vigencia legada = tributo como despesa variavel (selo "legado" e formulario antigo). Vigencia sem `modo` nao e tratada como legada. */
export const ehLegada = (v: Pick<VigenciaResponse, 'modo'>): boolean => v.modo === 'LEGADO';

/** Valores do cadastro da tabela (identificador so na criacao). */
export interface TabelaEntrada {
  codigo: string;
  nome: string;
  isDefault: boolean;
  ativa: boolean;
  /** P3: o preco praticado digitado ja e o total da NF (venda a nao contribuinte). Padrao: desmarcado. */
  precoIncluiCbsIbs: boolean;
}

export const tabelaVazia = (): TabelaEntrada => ({ codigo: '', nome: '', isDefault: false, ativa: true, precoIncluiCbsIbs: false });

export const entradaDeTabela = (t: TabelaPrecoResponse): TabelaEntrada => ({
  codigo: t.codigo,
  nome: t.nome,
  isDefault: t.isDefault,
  ativa: t.ativa,
  precoIncluiCbsIbs: t.precoIncluiCbsIbs,
});

export function validarCriacao(e: TabelaEntrada): Resultado<{ codigo: string; nome: string; isDefault: boolean; ativa: boolean; precoIncluiCbsIbs: boolean }> {
  const r = criarTabelaPrecoSchema.safeParse(e);
  if (!r.success) return { ok: false, mensagem: r.error.issues[0]?.message ?? 'Confira identificador e nome' };
  return { ok: true, dados: r.data };
}

/** Texto do erro da API para o usuario. VIGENCIA_RETROATIVA ganha mensagem propria. */
export function mensagemErroTabela(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VIGENCIA_RETROATIVA') {
      return 'Início retroativo não é permitido: a nova vigência começa hoje ou depois. Nada muda no passado.';
    }
    return `${e.message}${e.code ? ` (${e.code})` : ''}`;
  }
  return 'falha de comunicação com a API';
}
